import { rootApi } from 'src/services/rootApi';
import { HTTPRequestType } from 'src/services/api.types';

/**
 * AI note enrichment (Phase 3, JT1-1764 to JT1-1767) and suitable jobs (JT1-1771). The shapes
 * mirror `AiEnrichmentController` and `SuitableJobsService` in the backend.
 */

export type EnrichmentRecordType = 'candidate' | 'contact' | 'company' | 'deal' | 'job';

export interface ProfileSuggestion {
  id: string;
  record_type: EnrichmentRecordType;
  record_id: string;
  /** A field name from the backend's enrichment-fields.ts, e.g. notice_period. */
  field: string;
  label: string;
  before: unknown;
  after: unknown;
  status: 'pending' | 'accepted' | 'rejected' | 'auto_applied' | 'superseded';
  comment_id: string | null;
  created_at: string;
}

export type TaskProposalType = 'call' | 'email' | 'to_do' | 'general';

export interface TaskProposal {
  id: string;
  record_type: EnrichmentRecordType;
  record_id: string;
  title: string;
  type: TaskProposalType;
  due_at: string | null;
  due_has_time: boolean;
  assignee: { id: string; name: string | null } | null;
  status: 'pending' | 'accepted' | 'rejected';
  task_id: string | null;
  comment_id: string | null;
  created_at: string;
}

export interface RecordFindings {
  suggestions: ProfileSuggestion[];
  /** The newest AI fill per field: where a value came from. */
  filled: ProfileSuggestion[];
  tasks: TaskProposal[];
}

export interface NoteFindings {
  comment_id: string;
  record_type: EnrichmentRecordType;
  record_id: string;
  suggestions: ProfileSuggestion[];
  tasks: TaskProposal[];
}

export interface TaskProposalEdits {
  title?: string;
  due_at?: string | null;
  assigned_to?: string | null;
  type?: TaskProposalType;
}

export interface SuitableJob {
  jobId: string;
  title: string;
  score: number;
  reason: string;
  onJob: { applicationId: string; stage: string } | null;
}

export interface SuitableJobsResult {
  candidateId: string;
  jobs: SuitableJob[];
  notice: string | null;
}

/** The record page's own data changes when a suggestion is accepted, so it is refetched too. */
const RECORD_TAGS: Record<string, (id: string) => any[]> = {
  candidate: (id) => [{ type: 'OrgCandidates', id }, 'CandidateProfile'],
  contact: (id) => [{ type: 'OrgContacts', id }],
  company: (id) => [{ type: 'Companies', id }],
};

export const aiEnrichmentApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getRecordFindings: builder.query<RecordFindings, { type: EnrichmentRecordType; id: string }>({
      // Background reads: a refusal hides the card rather than opening the Feature Locked dialog.
      query: ({ type, id }) => ({ url: `/ai/enrichment/records/${type}/${id}`, silentFeatureCheck: true }),
      providesTags: ['AiFindings'],
    }),

    getNoteFindings: builder.query<NoteFindings[], string[]>({
      query: (commentIds) => ({ url: '/ai/enrichment/notes', params: { comment_ids: commentIds.join(',') }, silentFeatureCheck: true }),
      providesTags: ['AiFindings'],
    }),

    acceptSuggestion: builder.mutation<ProfileSuggestion, ProfileSuggestion>({
      query: (suggestion) => ({ url: `/ai/enrichment/suggestions/${suggestion.id}/accept`, method: HTTPRequestType.POST }),
      invalidatesTags: (_result, _error, suggestion) => ['AiFindings', ...(RECORD_TAGS[suggestion.record_type]?.(suggestion.record_id) ?? [])],
    }),

    rejectSuggestion: builder.mutation<ProfileSuggestion, ProfileSuggestion>({
      query: (suggestion) => ({ url: `/ai/enrichment/suggestions/${suggestion.id}/reject`, method: HTTPRequestType.POST }),
      invalidatesTags: ['AiFindings'],
    }),

    acceptAllSuggestions: builder.mutation<{ accepted: number }, { type: EnrichmentRecordType; id: string }>({
      query: ({ type, id }) => ({ url: `/ai/enrichment/records/${type}/${id}/accept-all`, method: HTTPRequestType.POST }),
      invalidatesTags: (_result, _error, { type, id }) => ['AiFindings', ...(RECORD_TAGS[type]?.(id) ?? [])],
    }),

    acceptTaskProposal: builder.mutation<TaskProposal, { id: string; edits?: TaskProposalEdits }>({
      query: ({ id, edits }) => ({ url: `/ai/enrichment/tasks/${id}/accept`, method: HTTPRequestType.POST, data: edits ?? {} }),
      invalidatesTags: ['AiFindings', 'Tasks'],
    }),

    rejectTaskProposal: builder.mutation<TaskProposal, string>({
      query: (id) => ({ url: `/ai/enrichment/tasks/${id}/reject`, method: HTTPRequestType.POST }),
      invalidatesTags: ['AiFindings'],
    }),

    getSuitableJobs: builder.query<SuitableJobsResult, string>({
      query: (candidateId) => ({ url: `/ai/candidates/${candidateId}/matching-jobs`, silentFeatureCheck: true }),
      providesTags: (_result, _error, candidateId) => [{ type: 'SuitableJobs', id: candidateId }],
    }),
  }),
});

export const {
  useGetRecordFindingsQuery,
  useGetNoteFindingsQuery,
  useAcceptSuggestionMutation,
  useRejectSuggestionMutation,
  useAcceptAllSuggestionsMutation,
  useAcceptTaskProposalMutation,
  useRejectTaskProposalMutation,
  useGetSuitableJobsQuery,
} = aiEnrichmentApi;
